import { useMemo, useState } from "react";
import type { ArticleDrawing } from "../lib/types.js";
import { ImageOffIcon } from "../lib/icons.js";

/**
 * Mini-rozkres na detailu dílu — image + překryv s hotspoty
 * aktuálního dílu (zvýrazněné oranžově) a kulatým label-number.
 *
 * Re-use: logika Y-flip + HTML overlay labelu (kvůli aspect ratio),
 * zjednodušená verze `DrawingViewer` z DrawingPage.
 *
 * Props.drawing.hotspots jsou hotspoty POUZE toho jednoho dílu —
 * backend už vše předfiltroval podle articleId.
 */
export function PartDrawingPreview({
  drawing,
  onOpenFull,
}: {
  drawing: ArticleDrawing;
  onOpenFull?: () => void;
}): JSX.Element {
  const [imgLoaded, setImgLoaded] = useState(false);

  // Rozsah souřadnic — stejně jako v DrawingPage
  const coordMax = useMemo(() => {
    let max = 0;
    for (const h of drawing.hotspots) {
      if (h.endX != null && h.endX > max) max = h.endX;
      if (h.endY != null && h.endY > max) max = h.endY;
      if (h.startX != null && h.startX > max) max = h.startX;
      if (h.startY != null && h.startY > max) max = h.startY;
    }
    return max;
  }, [drawing.hotspots]);

  const frameMax = coordMax <= 1.001 ? 1 : coordMax;
  const viewBox = `0 0 ${frameMax} ${frameMax}`;

  if (!drawing.imageUrl) {
    return (
      <div className="w-full aspect-square bg-zinc-50 rounded-xl border border-zinc-200 flex items-center justify-center text-zinc-300">
        <ImageOffIcon size={36} />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpenFull}
      className="relative block w-full aspect-square bg-white rounded-xl border border-zinc-200 overflow-hidden group hover:border-ktm-orange-100 transition-colors"
      aria-label={`Otevřít výkres ${drawing.subgroupDescription}`}
    >
      <img
        src={drawing.imageUrl}
        alt={drawing.subgroupDescription}
        className="absolute inset-0 w-full h-full object-contain p-4"
        onLoad={() => setImgLoaded(true)}
        draggable={false}
      />
      {imgLoaded && (
        <>
          {/* SVG overlay s hotspoty (stretched) */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox={viewBox}
            preserveAspectRatio="none"
          >
            {drawing.hotspots.map((h) => {
              if (
                h.startX == null ||
                h.startY == null ||
                h.endX == null ||
                h.endY == null
              )
                return null;
              const x = Math.min(h.startX, h.endX);
              const w = Math.abs(h.endX - h.startX);
              const hh = Math.abs(h.endY - h.startY);
              const y = frameMax - Math.max(h.startY, h.endY);
              return (
                <rect
                  key={h.id}
                  x={x}
                  y={y}
                  width={w}
                  height={hh}
                  fill="rgba(255,102,0,0.28)"
                  stroke="rgba(255,102,0,0.9)"
                  strokeWidth={coordMax <= 1.001 ? 0.004 : Math.max(1, coordMax / 400)}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
          </svg>
          {/* HTML overlay — kulaté labely s číslem pozice */}
          {drawing.lineNumber != null && (
            <div className="absolute inset-0 pointer-events-none">
              {drawing.hotspots.map((h, idx) => {
                if (
                  h.startX == null ||
                  h.startY == null ||
                  h.endX == null ||
                  h.endY == null
                )
                  return null;
                const cx = ((h.startX + h.endX) / 2) / frameMax;
                const cyMath = (h.startY + h.endY) / 2;
                const cy = (frameMax - cyMath) / frameMax;
                return (
                  <div
                    key={`lbl-${idx}`}
                    className="absolute flex items-center justify-center rounded-full bg-ktm-orange text-white text-[11px] font-semibold shadow-lg ring-2 ring-white"
                    style={{
                      left: `${cx * 100}%`,
                      top: `${cy * 100}%`,
                      width: 22,
                      height: 22,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    {drawing.lineNumber}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {/* Overlay badge s informací o výkresu */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 pt-10 text-white text-left opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="text-[11px] uppercase tracking-wider text-white/70">
          {drawing.subgroupCode}
        </div>
        <div className="text-sm font-medium leading-tight mt-0.5">
          {drawing.subgroupDescription}
        </div>
        {drawing.modelDescription && (
          <div className="text-[11px] text-white/80 mt-0.5 truncate">
            {drawing.modelDescription}
          </div>
        )}
      </div>
    </button>
  );
}
